import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import {
  Button,
  ComboBoxInput,
  TextBlock,
  TextInput,
  ComboBoxOptions,
  formatCentsAsDollars,
  parseDollarsToCents,
  formatDollarsForDisplay,
} from 'bench-internal-theme';
import { colors } from 'bench-style-constants';

interface LedgerAccount {
  id: string;
  name: string;
}

const MIN_DATA_ENTRIES = 2;
const MAX_DATA_ENTRIES = 75;

let nextDataEntryKey = 1;
const getDataEntryKey = () => nextDataEntryKey++;

const DataEntriesTable = styled.div`
  display: grid;
  grid-column-gap: 2px;
  grid-row-gap: 4px;
  grid-template-columns: 1fr 88px 88px 32px 32px;
  grid-template-rows: 16px repeat(auto-fit, min-content);
  padding-bottom: 8px;
`;

const CurrencyInputStyled = styled(TextInput)<{ styleType?: 'debit' | 'credit' }>`
  text-align: right;
  color: ${({ styleType }) =>
    styleType === 'credit' ? colors.copper : styleType === 'debit' ? colors.bonsai : 'inherit'};
  &::placeholder {
    color: ${({ styleType }) =>
      styleType === 'credit' ? colors.copper : styleType === 'debit' ? colors.bonsai : 'inherit'};
  }
`;

interface DataEntryInput {
  id?: string;
  key?: string;
  amountInCents?: number;
  ledgerAccount?: LedgerAccount;
}

interface DataEntryWithAmounts {
  id?: string;
  key?: string;
  ledgerAccount?: LedgerAccount;
  debitAmountInCents: number;
  creditAmountInCents: number;
}

interface DataEntryFieldsProps {
  defaultDataEntries?: DataEntryInput[];
  ledgerOptions: ComboBoxOptions;
}

interface DataEntryFieldsState {
  dataEntries: DataEntryWithAmounts[];
  focusedField: { index: number; type: 'debit' | 'credit' } | null;
  editingValue: string; // Raw input string while editing
}

class DataEntryFields extends Component<DataEntryFieldsProps, DataEntryFieldsState> {
  static defaultProps = {
    defaultDataEntries: undefined,
  };

  constructor(props: DataEntryFieldsProps) {
    super(props);
    const { defaultDataEntries } = props;
    const dataEntries = (defaultDataEntries || []).map(entry => ({
      id: entry.id,
      key: entry.key || getDataEntryKey().toString(),
      ledgerAccount: entry.ledgerAccount,
      debitAmountInCents: entry.amountInCents && entry.amountInCents > 0 ? entry.amountInCents : 0,
      creditAmountInCents:
        entry.amountInCents && entry.amountInCents < 0 ? Math.abs(entry.amountInCents) : 0,
    }));

    const numDataEntriesToCreate = MIN_DATA_ENTRIES - dataEntries.length;
    const newDataEntries: DataEntryWithAmounts[] = [];
    while (newDataEntries.length < numDataEntriesToCreate) {
      newDataEntries.push({
        key: getDataEntryKey().toString(),
        debitAmountInCents: 0,
        creditAmountInCents: 0,
      });
    }
    this.state = {
      dataEntries: [...dataEntries, ...newDataEntries],
      focusedField: null,
      editingValue: '',
    };
  }

  calculateRemainingBalance = (excludeIndex: number): number => {
    const { dataEntries } = this.state;
    let totalDebits = 0;
    let totalCredits = 0;

    dataEntries.forEach((entry, index) => {
      if (index !== excludeIndex) {
        totalDebits += entry.debitAmountInCents || 0;
        totalCredits += entry.creditAmountInCents || 0;
      }
    });

    // Positive means more debits than credits (need credit to balance)
    // Negative means more credits than debits (need debit to balance)
    return totalDebits - totalCredits;
  };

  handleFillBalance = (index: number) => {
    const { dataEntries } = this.state;
    const remainingBalance = this.calculateRemainingBalance(index);

    if (remainingBalance === 0) {
      return;
    }

    const newDataEntries = [...dataEntries];
    if (remainingBalance < 0) {
      // Need a debit to balance (more credits than debits)
      newDataEntries[index] = {
        ...newDataEntries[index],
        debitAmountInCents: Math.abs(remainingBalance),
        creditAmountInCents: 0,
      };
    } else {
      // Need a credit to balance (more debits than credits)
      newDataEntries[index] = {
        ...newDataEntries[index],
        debitAmountInCents: 0,
        creditAmountInCents: Math.abs(remainingBalance),
      };
    }
    this.setState({ dataEntries: newDataEntries });
  };

  handleAmountFocus = (index: number, type: 'debit' | 'credit') => {
    const { dataEntries } = this.state;
    const entry = dataEntries[index];
    const amountInCents = type === 'debit' ? entry.debitAmountInCents : entry.creditAmountInCents;
    // Initialize editingValue with current amount in dollars format
    const editingValue = amountInCents > 0 ? formatCentsAsDollars(amountInCents) : '';
    this.setState({ focusedField: { index, type }, editingValue });
  };

  handleAmountBlur = () => {
    const { dataEntries, focusedField, editingValue } = this.state;
    if (!focusedField) {
      return;
    }

    const { index, type } = focusedField;
    const amountInCents = parseDollarsToCents(editingValue) || 0;
    const newDataEntries = [...dataEntries];

    if (type === 'debit') {
      newDataEntries[index] = {
        ...newDataEntries[index],
        debitAmountInCents: amountInCents,
        // Clear credit if debit is being entered
        creditAmountInCents: amountInCents > 0 ? 0 : newDataEntries[index].creditAmountInCents,
      };
    } else {
      newDataEntries[index] = {
        ...newDataEntries[index],
        creditAmountInCents: amountInCents,
        // Clear debit if credit is being entered
        debitAmountInCents: amountInCents > 0 ? 0 : newDataEntries[index].debitAmountInCents,
      };
    }

    this.setState({ dataEntries: newDataEntries, focusedField: null, editingValue: '' });
  };

  handleAmountChange = (value: string) => {
    // Store raw input string - parsing happens on blur
    this.setState({ editingValue: value });
  };

  addDataEntry = () => {
    this.setState({
      dataEntries: [
        ...this.state.dataEntries,
        {
          key: getDataEntryKey().toString(),
          debitAmountInCents: 0,
          creditAmountInCents: 0,
        },
      ],
    });
  };

  removeDataEntry = (index: number) => {
    const { dataEntries } = this.state;
    const newDataEntries = [...dataEntries];
    newDataEntries.splice(index, 1);
    this.setState({ dataEntries: newDataEntries });
  };

  formatAmountForDisplay = (amountInCents: number): string => {
    if (amountInCents === 0) {
      return '';
    }
    return formatDollarsForDisplay(formatCentsAsDollars(amountInCents));
  };

  render() {
    const { ledgerOptions } = this.props;
    const { dataEntries, focusedField, editingValue } = this.state;

    const canDataEntriesBeRemoved = dataEntries.length <= MIN_DATA_ENTRIES;
    const canDataEntriesBeAdded = dataEntries.length >= MAX_DATA_ENTRIES;

    // Calculate if there's an imbalance (to enable/disable fill balance buttons)
    const totalDebits = dataEntries.reduce((sum, e) => sum + (e.debitAmountInCents || 0), 0);
    const totalCredits = dataEntries.reduce((sum, e) => sum + (e.creditAmountInCents || 0), 0);
    const hasImbalance = totalDebits !== totalCredits;

    return (
      <div>
        <DataEntriesTable>
          <TextBlock color={colors.cloud} styleType="small" uppercase>
            Ledgers
          </TextBlock>
          <TextBlock color={colors.cloud} styleType="small" uppercase>
            Debit
          </TextBlock>
          <TextBlock color={colors.cloud} styleType="small" uppercase>
            Credit
          </TextBlock>
          <div>{/* Fill Balance Button Heading Cell */}</div>
          <div>{/* Delete Button Heading Cell */}</div>
          {dataEntries.map(
            ({ id, key, ledgerAccount, debitAmountInCents, creditAmountInCents }, index) => {
              const isDebitFocused =
                focusedField && focusedField.index === index && focusedField.type === 'debit';
              const isCreditFocused =
                focusedField && focusedField.index === index && focusedField.type === 'credit';
              const rowHasNoAmount = debitAmountInCents === 0 && creditAmountInCents === 0;

              return (
                <Fragment key={id || key}>
                  {id && <input value={id} name={`dataEntries[][${index}][id]`} type="hidden" />}
                  <ComboBoxInput
                    defaultValue={
                      ledgerAccount &&
                      ledgerOptions.find(option => option.value === ledgerAccount.id)
                    }
                    name={`dataEntries[][${index}][ledgerAccountId]`}
                    options={ledgerOptions}
                    isSearchable
                    backspaceRemovesValue={false}
                  />
                  <CurrencyInputStyled
                    type={isDebitFocused ? 'number' : 'text'}
                    step=".01"
                    max="99999999.99"
                    placeholder="-"
                    styleType="debit"
                    value={
                      isDebitFocused
                        ? editingValue
                        : this.formatAmountForDisplay(debitAmountInCents)
                    }
                    onFocus={() => this.handleAmountFocus(index, 'debit')}
                    onBlur={this.handleAmountBlur}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      this.handleAmountChange(e.target.value)
                    }
                  />
                  <input
                    type="hidden"
                    name={`dataEntries[][${index}][debitAmount]`}
                    value={debitAmountInCents || ''}
                  />
                  <CurrencyInputStyled
                    type={isCreditFocused ? 'number' : 'text'}
                    step=".01"
                    max="99999999.99"
                    placeholder="-"
                    styleType="credit"
                    value={
                      isCreditFocused
                        ? editingValue
                        : this.formatAmountForDisplay(creditAmountInCents)
                    }
                    onFocus={() => this.handleAmountFocus(index, 'credit')}
                    onBlur={this.handleAmountBlur}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      this.handleAmountChange(e.target.value)
                    }
                  />
                  <input
                    type="hidden"
                    name={`dataEntries[][${index}][creditAmount]`}
                    value={creditAmountInCents || ''}
                  />
                  <Button
                    disabled={!hasImbalance || !rowHasNoAmount}
                    size="medium"
                    styleType="ghost"
                    type="button"
                    title="Fill remaining balance"
                    onClick={() => this.handleFillBalance(index)}
                  >
                    Δ
                  </Button>
                  <Button
                    disabled={canDataEntriesBeRemoved}
                    icon="close"
                    size="medium"
                    styleType="total-ghost"
                    type="button"
                    onClick={() => this.removeDataEntry(index)}
                  />
                </Fragment>
              );
            },
          )}
        </DataEntriesTable>
        <Button
          disabled={canDataEntriesBeAdded}
          icon="plus"
          size="xsmall"
          styleType="total-ghost"
          type="button"
          onClick={this.addDataEntry}
        >
          Add Data Entry
        </Button>
      </div>
    );
  }
}

export default DataEntryFields;
